import { z } from 'zod';
import type { TFunction } from 'i18next';

import type { ReceiveReleaseContextDto } from '@/entities/receive';

import { isReleaseLineEmpty, releaseLineKey } from '../lib/build-payload';
import { RELEASE_GOODS_TEXT_MAX_LENGTH } from './constants';

const lineSchema = z.object({
  receiveShipmentId: z.string(),
  lineId: z.string(),
  goodQty: z.number(),
  damagedQty: z.number()
});

/**
 * Mirrors what the backend rejects, so the operator hears about it before a
 * round trip: the date window, the required handover fields, and no line above
 * what is releasable.
 */
export const getReleaseGoodsSchema = (t: TFunction, context: ReceiveReleaseContextDto, maxReleaseDate: string) => {
  const required = t('validity.valueMissing.defaultMessage');
  const maxText = (field: string) =>
    z.string().max(
      RELEASE_GOODS_TEXT_MAX_LENGTH,
      t('receive.confirm.error.maxLength', '{{field}} must be at most {{max}} characters', {
        field,
        max: RELEASE_GOODS_TEXT_MAX_LENGTH
      })
    );
  const requiredText = (field: string) => maxText(field).refine((value) => value.trim().length > 0, required);

  return z
    .object({
      releaseDate: z.string().min(1, required),
      receptionDocumentNumber: maxText(t('shipment.receptionDocumentNumber.alt1.short', 'Document Number')),
      transporterCompanyReleasedTo: requiredText(
        t('shipment.transporterCompanyReleasedTo.label', 'Transport Company or Organization Released to')
      ),
      driverReleasedTo: requiredText(t('shipment.driverReleasedTo.label', 'Actual Person Items Release to')),
      receptionVerificationCode: z.enum(['D', 'P', 'E']),
      remarks: maxText(t('shipment.releaseremarks.label', 'Additional details')),
      printGoodsReleaseNote: z.boolean(),
      lines: z.array(lineSchema)
    })
    .superRefine((values, ctx) => {
      // Dates are `yyyy-MM-dd`, so string comparison is chronological.
      if (values.releaseDate > maxReleaseDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['releaseDate'],
          message: t('receive.release.error.dateInFuture', 'Release date is in the future')
        });
      } else if (values.releaseDate && values.releaseDate < context.minReleaseDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['releaseDate'],
          message: t('receive.release.error.dateBeforeMin', 'Release date cannot be before {{date}}', {
            date: context.minReleaseDate
          })
        });
      }

      const linesByKey = new Map(context.releasableLines.map((line) => [releaseLineKey(line), line]));

      values.lines.forEach((entered, index) => {
        const line = linesByKey.get(releaseLineKey(entered));
        if (!line) return;

        (['goodQty', 'damagedQty'] as const).forEach((field) => {
          const value = entered[field];
          if (!Number.isInteger(value) || value < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['lines', index, field],
              message: t('receive.confirm.error.nonNegativeInteger', 'Enter a whole number of 0 or more')
            });
          } else if (value > line[field]) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['lines', index, field],
              message: t('receive.release.error.aboveReceived', 'Above the received quantity ({{received}})', {
                received: line[field]
              })
            });
          }
        });
      });

      // Legacy closes the dialog and silently releases nothing; saying so is kinder.
      if (values.lines.every(isReleaseLineEmpty)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines'],
          message: t('receive.release.error.nothingToRelease', 'Enter at least one quantity to release')
        });
      }
    });
};
